import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { LoadingSkeleton } from "@/components/states/LoadingSkeleton";
import { EmptyState } from "@/components/states/EmptyState";
import { ErrorState } from "@/components/states/ErrorState";
import { DollarSign } from "lucide-react";

type Budget = {
  axis: string;
  metric_key: string;
  target: number;
  ceiling: number;
  current_value: number;
  unit: string;
};
type DailyRow = { day: string; usd_per_1k_turns: number; turns: number; cache_hits: number };

const AXIS_LABEL: Record<string, string> = {
  cost: "Custo",
  latency: "Latência",
  duplication: "Duplicação",
  clarity: "Clareza",
};

export default function SistemaCusto() {
  const { user } = useAuth();
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const { data: m, error: mErr } = await supabase
        .from("org_members")
        .select("org_id")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();
      if (mErr) throw mErr;
      if (!m) {
        setLoading(false);
        return;
      }
      const [{ data: b, error: bErr }, { data: d, error: dErr }] = await Promise.all([
        supabase.from("cost_budget").select("*").eq("org_id", m.org_id),
        supabase
          .from("cost_per_1k_turns_daily")
          .select("*")
          .eq("org_id", m.org_id)
          .order("day", { ascending: true })
          .limit(30),
      ]);
      if (bErr) throw bErr;
      if (dErr) throw dErr;
      setBudgets((b as Budget[]) ?? []);
      setDaily((d as DailyRow[]) ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar métricas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const statusVariant = (current: number, target: number, ceiling: number): { label: string; cls: string } => {
    if (current <= target) return { label: "OK", cls: "border-primary/40 bg-primary/10 text-primary" };
    if (current <= ceiling) return { label: "Atenção", cls: "border-warning/40 bg-warning/10 text-warning-foreground" };
    return { label: "Crítico", cls: "border-destructive/40 bg-destructive/10 text-destructive" };
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Custo &amp; eficiência</h1>
        <p className="text-muted-foreground">USD por 1k turnos · 4 eixos com tetos formais</p>
      </div>

      {loading && <LoadingSkeleton shape="kpi" count={4} />}

      {!loading && error && <ErrorState message={error} onRetry={load} />}

      {!loading && !error && budgets.length === 0 && (
        <EmptyState
          icon={DollarSign}
          title="Sem orçamentos configurados"
          description="Os tetos por eixo (custo, latência, duplicação, clareza) ainda não foram criados para esta organização."
        />
      )}

      {!loading && !error && budgets.length > 0 && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {budgets.map((b) => {
              const pct = b.ceiling > 0 ? Math.min(100, (b.current_value / b.ceiling) * 100) : 0;
              const v = statusVariant(b.current_value, b.target, b.ceiling);
              return (
                <Card key={`${b.axis}-${b.metric_key}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center justify-between">
                      {AXIS_LABEL[b.axis] ?? b.axis}
                      <Badge variant="outline" className={v.cls}>{v.label}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {b.current_value.toFixed(2)}{" "}
                      <span className="text-sm font-normal text-muted-foreground">{b.unit}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Alvo {b.target} / Teto {b.ceiling}
                    </div>
                    <Progress value={pct} className="mt-3" />
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>USD por 1k turnos — últimos 30 dias</CardTitle>
            </CardHeader>
            <CardContent className="h-72">
              {daily.length === 0 ? (
                <EmptyState title="Sem dados na janela" description="Aguarde a próxima agregação noturna." />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis
                      dataKey="day"
                      tickFormatter={(v) =>
                        new Date(v).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
                      }
                    />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="usd_per_1k_turns" stroke="hsl(var(--primary))" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Otimizações ativas</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center justify-between">
                  <span>OPT-C1 · Cache de embeddings (TTL 24h)</span>
                  <Badge variant="secondary">ativo</Badge>
                </li>
                <li className="flex items-center justify-between">
                  <span>OPT-C2 · Consolidação noturna em batch</span>
                  <Badge variant="secondary">ativo</Badge>
                </li>
                <li className="flex items-center justify-between">
                  <span>OPT-C3 · Short-circuit em alta confiança (≥0.85)</span>
                  <Badge variant="secondary">ativo</Badge>
                </li>
                <li className="flex items-center justify-between">
                  <span>OPT-C6 · k dinâmico em recuperação</span>
                  <Badge variant="secondary">ativo</Badge>
                </li>
              </ul>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
